import React, { useState, useRef, useEffect } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';
import { Tooltip } from 'components/ui/tooltip';
import styles from './CollapsibleSection.module.scss';

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  tooltipContent?: string;
  forceExpanded?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultOpen = false,
  tooltipContent,
  forceExpanded = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const [needsToggle, setNeedsToggle] = useState(true);

  useEffect(() => {
    if (forceExpanded && contentRef.current) {
      // Check if content height exceeds a reasonable threshold
      const contentHeight = contentRef.current.scrollHeight;
      const shouldShowToggle = contentHeight > 200;
      setNeedsToggle(shouldShowToggle);

      if (!shouldShowToggle) {
        setIsOpen(true);
      }
    }
  }, [forceExpanded, children]);

  const handleToggle = () => {
    if (needsToggle || !forceExpanded) {
      setIsOpen(!isOpen);
    }
  };

  const showToggleIcon = needsToggle || !forceExpanded;

  return (
    <div className={styles.container}>
      <button
        className={styles.header}
        onClick={handleToggle}
        aria-expanded={isOpen}
        disabled={!showToggleIcon}
      >
        <div className={styles.titleContainer}>
          <span
            className={styles.icon}
            style={{ visibility: showToggleIcon ? 'visible' : 'hidden' }}
          >
            {isOpen ? (
              <IoChevronDown size={20} />
            ) : (
              <IoChevronForward size={20} />
            )}
          </span>
          <h3 className={styles.title}>{title}</h3>
          <span
            className={styles.infoIcon}
            style={{ visibility: tooltipContent ? 'visible' : 'hidden' }}
          >
            {tooltipContent && (
              <Tooltip content={tooltipContent} openDelay={0}>
                <MdInfoOutline size={16} />
              </Tooltip>
            )}
          </span>
        </div>
      </button>

      <div
        ref={contentRef}
        className={`${styles.content} ${isOpen ? styles.open : styles.closed}`}
      >
        {children}
      </div>
    </div>
  );
};

export default CollapsibleSection;
